import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { clamp01, formatPercent } from '../../lib/format';
import type { ResourceKey, Severity } from '../../lib/game';

export type ProgressAccent = ResourceKey | Severity | 'neutral';

export interface ProgressBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Completion ratio, 0..1. Values outside the range are clamped. */
  value: number;
  accent?: ProgressAccent;
  size?: 'xs' | 'sm' | 'md';
  /** Label rendered above the track. */
  label?: ReactNode;
  /** Right-aligned value shown next to the label — a countdown or "68 %". */
  hint?: ReactNode;
  /** Animates the energy sweep — use while a job is actively running. */
  animated?: boolean;
  /** Accessible name when no visible `label` is given. */
  'aria-label'?: string;
}

const FILLS: Record<ProgressAccent, string> = {
  titan: 'bg-titan shadow-glow-titan',
  silicate: 'bg-silicate shadow-glow-silicate',
  isotope: 'bg-isotope shadow-glow-isotope',
  power: 'bg-power shadow-glow-power',
  aether: 'bg-aether shadow-glow-aether',
  credit: 'bg-credit shadow-glow-credit',
  chronos: 'bg-chronos shadow-glow-chronos',
  danger: 'bg-danger shadow-glow-danger',
  warning: 'bg-warning',
  success: 'bg-success shadow-glow-credit',
  info: 'bg-info',
  neutral: 'bg-obsidian-400',
};

const HEIGHTS = {
  xs: 'h-1',
  sm: 'h-1.5',
  md: 'h-2.5',
} as const;

/**
 * Horizontal progress track.
 *
 * Used by the build/research queue, XP bars, shield integrity and storage
 * capacity. The fill carries the accent's glow so a bar reads as an energy
 * channel rather than a flat meter.
 */
export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(function ProgressBar(
  {
    value,
    accent = 'silicate',
    size = 'sm',
    label,
    hint,
    animated = false,
    className,
    ...rest
  },
  ref,
) {
  const ratio = clamp01(value);
  const percent = formatPercent(ratio);

  return (
    <div ref={ref} className={cn('w-full', className)} {...rest}>
      {label || hint ? (
        <div className="mb-1 flex items-baseline justify-between gap-2">
          {label ? (
            <span className="truncate font-display text-label uppercase text-content-muted">
              {label}
            </span>
          ) : (
            <span />
          )}
          {hint ? (
            <span className="shrink-0 text-label text-content-secondary" data-sg-numeric>
              {hint}
            </span>
          ) : null}
        </div>
      ) : null}

      <div
        role="progressbar"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={rest['aria-label']}
        className={cn(
          'relative w-full overflow-hidden rounded-pill border border-edge-subtle bg-surface-base',
          HEIGHTS[size],
        )}
      >
        <div
          className={cn(
            'h-full rounded-pill transition-[width] duration-500 ease-hud',
            FILLS[accent],
          )}
          style={{ width: percent }}
        >
          {animated && ratio > 0 ? (
            <span
              aria-hidden
              className="block h-full w-full animate-energy-flow bg-energy bg-[length:200%_100%] opacity-70"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
});

export interface SegmentedBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Filled segment count. */
  value: number;
  /** Total segment count. */
  max: number;
  accent?: ProgressAccent;
  label?: ReactNode;
}

const SEGMENT_FILLS: Record<ProgressAccent, string> = {
  titan: 'bg-titan',
  silicate: 'bg-silicate',
  isotope: 'bg-isotope',
  power: 'bg-power',
  aether: 'bg-aether',
  credit: 'bg-credit',
  chronos: 'bg-chronos',
  danger: 'bg-danger',
  warning: 'bg-warning',
  success: 'bg-success',
  info: 'bg-info',
  neutral: 'bg-obsidian-400',
};

/**
 * Discrete stepped bar — for things counted in whole units: building levels,
 * talent points spent in a path, Phalanx tiers.
 */
export const SegmentedBar = forwardRef<HTMLDivElement, SegmentedBarProps>(
  function SegmentedBar({ value, max, accent = 'silicate', label, className, ...rest }, ref) {
    const filled = Math.max(0, Math.min(max, Math.round(value)));

    return (
      <div ref={ref} className={cn('w-full', className)} {...rest}>
        {label ? (
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <span className="font-display text-label uppercase text-content-muted">{label}</span>
            <span className="text-label text-content-secondary" data-sg-numeric>
              {filled}/{max}
            </span>
          </div>
        ) : null}
        <div
          className="flex gap-0.5"
          role="progressbar"
          aria-valuenow={filled}
          aria-valuemin={0}
          aria-valuemax={max}
        >
          {Array.from({ length: max }, (_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 flex-1 rounded-[1px] transition-colors duration-200',
                i < filled ? SEGMENT_FILLS[accent] : 'bg-surface-raised',
              )}
            />
          ))}
        </div>
      </div>
    );
  },
);
